import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@/utils/errorMessage';
import { UserService } from '../user.service';
import { getUserData, removeUserData, setUserData } from '@/utils/cookies';
import { clearUserScopedQueryCache } from '@/utils/clearUserScopedQueryCache';
import { clearSchedulingPayDraftSilently } from '@/utils/schedulingPayAfterAuth';
import { logoutBeforeLoginIfNeeded } from '@/utils/logoutBeforeLogin';

export const usePatchUserBanned = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const patchUserBannedMutation = useMutation({
		mutationFn: (data: Users.PatchBanned) => UserService.patchBanned(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users'] });
			toast.success('Usuário atualizado com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro na atualização do usuário'),
			);
		},
	});

	return patchUserBannedMutation;
};

export const usePatchUserResetCredits = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => UserService.patchResetCredits(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users'] });
			toast.success('Créditos do usuário resetados com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao resetar créditos'));
		},
	});
};

export const usePatchUser = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const patchUserBannedMutation = useMutation({
		mutationFn: (data: Partial<Users.Update> & { id: string }) =>
			UserService.patch(data),
		onSuccess: (userUpdated) => {
			queryClient.invalidateQueries({ queryKey: ['users'] });
			const me = getUserData();
			if (me?.id && userUpdated.id === me.id) {
				queryClient.setQueryData(
					['user', undefined, true],
					userUpdated,
				);
				setUserData(userUpdated);
			} else {
				queryClient.invalidateQueries({ queryKey: ['user'] });
			}
			toast.success('Usuário atualizado com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro na atualização do usuário'),
			);
		},
	});

	return patchUserBannedMutation;
};

export const useCreateUser = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const createUserBannedMutation = useMutation({
		mutationFn: (data: Partial<Users.Create>) =>
			UserService.create(data as Users.Create),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['users'] });
			toast.success('Cadastro realizado com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro na criação do usuário'));
		},
	});

	return createUserBannedMutation;
};

export const usePatchUserComplement = (
	onSuccess?: (data: Users.CreateResponse) => void,
) => {
	const queryClient = useQueryClient();

	const createUserCompleteMutation = useMutation({
		mutationFn: (data: Users.CreateComplement) =>
			UserService.patchComplement(data),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ['users'] });
			toast.success('Cadastro realizado com sucesso!');
			if (onSuccess) onSuccess(data);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro na criação do usuário'));
		},
	});

	return createUserCompleteMutation;
};

export const useLoginUser = (
	onSuccess?: (data: Users.CreateResponse) => void,
) => {
	const queryClient = useQueryClient();

	const loginUserMutation = useMutation({
		mutationFn: async (data: Users.Login) => {
			await logoutBeforeLoginIfNeeded(queryClient);
			return UserService.login(data);
		},
		onSuccess: async (data) => {
			await clearUserScopedQueryCache(queryClient);
			toast.success('Login realizado com sucesso!');
			setUserData(data.user);
			if (onSuccess) onSuccess(data);
		},
		onError: (_error: unknown) => {
			// No fluxo de login, evitamos expor mensagens detalhadas de validação
			// para não confundir o usuário com regras de senha ou dados sensíveis.
			// Sempre mostramos um erro genérico de credenciais inválidas.
			toast.error('E-mail/Senha inválidos');
		},
	});

	return loginUserMutation;
};

export const useLogoutUser = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	const logoutUserMutation = useMutation({
		mutationFn: () => UserService.logout(),
		onSuccess: async () => {
			await clearUserScopedQueryCache(queryClient);
			clearSchedulingPayDraftSilently();
			removeUserData();
			onSuccess?.();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro no logout.'));
		},
	});

	return logoutUserMutation;
};
